export const TEST_START_TIMEOUT_MS = 60_000;
export const TEST_RESULT_TIMEOUT_MS = 120_000;

export class EvaluationTestTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EvaluationTestTimeoutError";
  }
}

/** Bound the client request, including a connection that never returns a response. */
export function withTestDeadline<T>(
  task: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  message: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const error = new EvaluationTestTimeoutError(message);
      reject(error);
      controller.abort(error);
    }, timeoutMs);
    Promise.resolve().then(() => task(controller.signal)).then(
      (result) => { clearTimeout(timer); resolve(result); },
      (error) => { clearTimeout(timer); reject(error); },
    );
  });
}

/** Always dispose the listener, including on timeout and synchronous subscription callbacks. */
export function waitForTestResult<T extends { status: string }>(
  subscribe: (next: (result: T | undefined) => void, error: (error: Error) => void) => () => void,
  timeoutMs = TEST_RESULT_TIMEOUT_MS,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let unsubscribe: (() => void) | undefined;
    let settled = false;
    let lastStatus = "no result document received";
    const finish = (error?: Error, result?: T) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe?.();
      if (error) reject(error);
      else resolve(result!);
    };
    const timer = setTimeout(() => finish(new EvaluationTestTimeoutError(
      `Timed out waiting for the test result after ${timeoutMs / 1000} seconds (${lastStatus}). Automark may still be processing it; it has not been retried.`,
    )), timeoutMs);
    try {
      unsubscribe = subscribe((result) => {
        if (settled) return;
        lastStatus = result?.status ?? "no result document received";
        if (result?.status === "success" || result?.status === "failed") finish(undefined, result);
      }, (error) => finish(error));
      if (settled) unsubscribe();
    } catch (error) {
      finish(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

export function isTestTimeout(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const value = error as { name?: string; status?: number; statusCode?: number; cause?: { name?: string } };
  return value.name === "EvaluationTestTimeoutError" || value.name === "TimeoutError" ||
    value.cause?.name === "TimeoutError" || (value.statusCode ?? value.status) === 504;
}
